import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { validColorValidator } from 'ngx-colors';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { shareReplay, switchMap, take } from 'rxjs/operators';
import { BotGuild, ChannelConfig, ChannelPreview, ChannelSetupInfo, EventStartNotificationConfig } from 'src/app/models/bot/guild';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { GuildService } from 'src/app/services/guild.service';

interface EventStartNotificationDraft {
  identifier: string;
  state: string;
  type: string;
  time: string;
  timeInvalid: boolean;
  deleteWhen: string;
  channelId: string;
  channelText: string;
  channelEmbed: string;
  dmText: string;
  dmEmbed: string;
}

@Component({
  selector: 'app-channel-card',
  templateUrl: './channel-card.component.html',
  styleUrls: [
    '../shared/styles/guild-dashboard-shared.css',
    '../../../shared/styles/action-buttons.css',
    './channel-card.component.css'
  ]
})
export class ChannelCardComponent implements OnInit {
  faIcons = {
    trash: faTrash,
  };

  guildId: string;
  channelId: string;
  guild: BotGuild;
  isCreateMode = false;
  tempUserRoles: string[] = [];

  resetBehavior: string; // currently selected reset behavior
  countBehavior: string; // currently selected count behavior
  resetBehaviorCurrent: string; // currently saved reset behavior in backend. Used to determine if the reset behavior has changed.
  countBehaviorCurrent: string; // currently saved count behavior in backend. Used to determine if the count behavior has changed.
  // Export File Config
  exportFile: string;
  exportFileCurrent: string;
  exportSignupState: string;
  exportSignupStateCurrent: string;

  userDeleteBehavior: string;
  organizerDeleteBehavior: string;
  userDeleteBehaviorCurrent: string;
  organizerDeleteBehaviorCurrent: string;

  threadCreateStatus: boolean;
  threadAutoDelStatus: boolean;
  voiceName = '${event_title}';
  voiceTime = '5m';
  voiceCategoryId = '';
  voiceLimit: number | null = null;
  voiceViewRestriction = false;
  voiceConnectRestriction = false;
  voiceTimeInvalid = false;
  eventCreateNotifyChannelId = '';
  eventCreateNotifyMessage = '';
  eventCreateNotifyEmbed = '';
  eventCreateDeleteMode = 'never';
  eventCreateDeleteAfter = '';
  eventCreateDeleteInvalid = false;
  private eventCreateDefaults = {
    content: '',
    embed: '',
  };
  readonly startNotifyStateOptions = [
    { value: 'attending', label: 'Attending' },
    { value: 'attending role', label: 'Attending (Role)' },
    { value: 'attending status', label: 'Attending (Status)' },
    { value: 'not attending', label: 'Not attending' },
    { value: 'confirmed', label: 'Confirmed' },
  ];
  readonly startDeleteOptions = [
    { value: 'never', label: 'Never' },
    { value: 'event_start', label: 'When the event starts' },
    { value: 'event_end', label: 'When the event ends' },
    { value: 'event_deletion', label: 'When the event is deleted' },
  ];
  eventStartNotifyState = 'attending';
  eventStartNotifyType = 'Channel';
  eventStartNotifyChannelId = '';
  eventStartNotifyTime = '15m';
  eventStartNotifyTimeInvalid = false;
  eventStartAdding = false;
  eventStartSaving = false;
  eventStartEdit: EventStartNotificationDraft | null = null;
  eventStartNotifyChannelText = '';
  eventStartNotifyChannelEmbed = '';
  eventStartNotifyDmText = '';
  eventStartNotifyDmEmbed = '';
  eventStartDeleteWhen = 'never';
  private eventStartDefaults = {
    c_c: '',
    c_ec: '',
    dm_c: '',
    dm_ec: '',
  };
  eventCancelNotifyType = 'Channel';
  eventCancelNotifyTime = '15m';
  eventCancelNotifyTimeInvalid = false;
  eventCancelNotifySignups = 0;
  eventCancelNotifyChannelId = '';
  eventCancelDeleteMode = 'never';
  eventCancelDeleteAfter = '';
  eventCancelDeleteInvalid = false;
  loggingChannelId = '';
  logEventCreation = true;
  logEventEdit = true;
  logEventClosed = true;
  logEventOpened = true;
  logEventDeleted = true;
  logUserSignUp = true;
  logUserSignOff = true;
  setupTargetChannelId = '';
  textChannels: ChannelPreview[] = [];
  filteredExportChannels: ChannelPreview[] = [];
  filteredCreateChannels: ChannelPreview[] = [];
  filteredCancelChannels: ChannelPreview[] = [];
  filteredLogChannels: ChannelPreview[] = [];
  filteredStartChannels: ChannelPreview[] = [];
  filteredStartEditChannels: ChannelPreview[] = [];
  exportChannelFilter = new FormControl('');
  createChannelFilter = new FormControl('');
  cancelChannelFilter = new FormControl('');
  logChannelFilter = new FormControl('');
  startChannelFilter = new FormControl('');
  startEditChannelFilter = new FormControl('');
  readonly sameChannelId = (
    left: string | number | null | undefined,
    right: string | number | null | undefined,
  ): boolean => this.channelIdString(left) === this.channelIdString(right);
  setupChannels: ChannelPreview[] = [];
  filteredSetupChannels: ChannelPreview[] = [];
  setupChannelFilter = new FormControl('');
  setupChannelType = 'event';
  setupOrganizerRoleId = '';
  setupUserRoleId = '';
  channelSetupInfo: ChannelSetupInfo | null = null;
  channelToDeleteLabel = '';
  eventsImpacted: { name: string; id: string }[] = [];
  repeatingEventsImpacted: { name: string; id: string }[] = [];
  private dialogDeleteChannelRef: MatDialogRef<any>;
  private sectionFragment: string | null = null;
  @ViewChild('deleteChannelDialog') deleteChannelDialog: TemplateRef<any>;

  public eventCloseColorForm: FormGroup = new FormGroup(
    {
      closeColorInputCtrl: new FormControl("#000000", validColorValidator()),
      pickerCtrl: new FormControl("#000000"),
    },
    { updateOn: "change" }
  );

  @Input() channelInfo$: Observable<ChannelConfig>;
  channelSetupInfo$: Observable<ChannelSetupInfo>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private guildService: GuildService,
    private botService: BotResourceService,
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    this.exportChannelFilter.valueChanges.subscribe(() => this.applyExportChannelFilter());
    this.createChannelFilter.valueChanges.subscribe(() => this.applyCreateChannelFilter());
    this.cancelChannelFilter.valueChanges.subscribe(() => this.applyCancelChannelFilter());
    this.logChannelFilter.valueChanges.subscribe(() => this.applyLogChannelFilter());
    this.startChannelFilter.valueChanges.subscribe(() => this.applyStartChannelFilter());
    this.startEditChannelFilter.valueChanges.subscribe(() => this.applyStartEditChannelFilter());
    this.setupChannelFilter.valueChanges.subscribe(() => this.applySetupChannelFilter());
    this.route.fragment.subscribe(fragment => {
      this.sectionFragment = fragment;
      this.scrollToSection();
    });
    this.guildId = this.route.parent.snapshot.paramMap.get('id');
    this.guildService.getGuildData(this.guildId).subscribe(guild => {
      this.guild = guild;
    });

    // Angular reuses the component when navigating between routes within the same component.
    // This means that the component is not destroyed and recreated, so ngOnInit is not called again.
    // We therefore need to subscribe to the paramMap observable and update the component when the route changes.
    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap) => {
          this.channelId = params.get('channelId');
          this.isCreateMode = this.channelId === null || this.channelId === 'new';
          if (this.isCreateMode) {
            this.channelInfo$ = of(null as unknown as ChannelConfig);
            this.channelSetupInfo$ = this.botService.getGuildChannelSetupInfo(this.guildId).pipe(
              shareReplay(1)
            );
            this.channelSetupInfo$.pipe(take(1)).subscribe(info => {
              this.channelSetupInfo = info;
              this.initializeChannelSetupDefaults(info);
            });
            return of(null);
          }
          this.channelInfo$ = this.botService.getGuildChannel(this.guildId, this.channelId).pipe(
            shareReplay(1)
          );
          this.channelInfo$.subscribe(channel => {
            if (channel && channel.sign_up_numbers_behavior) {
              // console.log(channel.sign_up_numbers_behavior)
              this.countBehavior = Math.floor(channel.sign_up_numbers_behavior / 10).toString();
              this.resetBehavior = (channel.sign_up_numbers_behavior % 10).toString();
              this.countBehaviorCurrent = this.countBehavior;
              this.resetBehaviorCurrent = this.resetBehavior;
            } else {
              // Initialize with default values if not set
              this.countBehavior = '1'; // Default: Count every sign up
              this.resetBehavior = '0'; // Default: Do not reset
              this.countBehaviorCurrent = this.countBehavior;
              this.resetBehaviorCurrent = this.resetBehavior;
            }
            if (channel && channel.clean_mode) {
              this.organizerDeleteBehavior = Math.floor(Number(channel.clean_mode) / 10).toString();
              this.userDeleteBehavior = (Number(channel.clean_mode) % 10).toString();
              this.organizerDeleteBehaviorCurrent = this.organizerDeleteBehavior;
              this.userDeleteBehaviorCurrent = this.userDeleteBehavior;
            }
            if (channel) {
              this.textChannels = channel.channels || [];
              this.eventCloseColorForm.controls["closeColorInputCtrl"].setValue("#" + channel.closed_color, { emitEvent: false, })
              this.eventCloseColorForm.controls["pickerCtrl"].setValue("#" + channel.closed_color, { emitEvent: false, })
              if (!(this.guild.owner || channel.has_organizer_role)) {
                this.eventCloseColorForm.controls["closeColorInputCtrl"].disable()
                this.eventCloseColorForm.controls["pickerCtrl"].disable()
              }
              this.threadCreateStatus = channel.use_threads
              this.threadAutoDelStatus = channel.thread_auto_del
              // Export File Settings
              this.exportFile = channel.export_file.toString()
              this.exportFileCurrent = channel.export_file.toString()
              this.exportSignupState = channel.export_signup_state.toString()
              this.exportSignupStateCurrent = channel.export_signup_state.toString()
              this.loadVoiceSettings(channel);
              this.loadNotificationAndLoggingSettings(channel);
              this.refreshTextChannelFilters();
              this.scrollToSection();
            }
          });
          return of(null)
        }),
      )
      .subscribe(
        () => {
          // console.log(this.channelId)
          // console.log(this.guildId)
          // console.log(this.channelInfo$.subscribe(channel => console.log(channel)))
          // console.log(this.guild)
        }
      );

      this.eventCloseColorForm.controls["closeColorInputCtrl"].valueChanges.subscribe((color) => {
        // Called on manual value change
        // Call picker because picker updates the view field
        if (this.eventCloseColorForm.controls["pickerCtrl"].valid) {
          this.eventCloseColorForm.controls["pickerCtrl"].setValue(color, {
            emitEvent: false,
          });
        }
      });
      this.eventCloseColorForm.controls["pickerCtrl"].valueChanges.subscribe((color) => {
        // Called on color select via picker
        // Update view value
        this.eventCloseColorForm.controls["closeColorInputCtrl"].setValue(color, {
          emitEvent: false,
        });
        if (this.eventCloseColorForm.controls["closeColorInputCtrl"].valid) {
          this.botService.sendCommand(this.guildId, 'event_close color set', this.eventCloseColorForm.controls["closeColorInputCtrl"].value, this.channelId);
        }
      });
  }

  setOrganizerRole(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'channel organizer set', (eventTarget as HTMLInputElement).value, this.channelId);
  }

  setExportChannel(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'export_event channel set', (eventTarget as HTMLInputElement).value, this.channelId);
  }

  setEventCloseTime(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'event_close time set', (eventTarget as HTMLInputElement).value, this.channelId);
  }

  setEventCloseBehavior(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'event_close behavior set', (eventTarget as HTMLInputElement).value, this.channelId);
  }

  onUserRolesChanged(selectedRoleIds: string[]): void {
    this.tempUserRoles = selectedRoleIds;
  }

  setUserRoles(): void {
    const max = this.guild.has_premium ? 25 : 1;
    const selectedRoleIds = this.tempUserRoles;

    // Enforce limits
    if (selectedRoleIds.length < 1 || selectedRoleIds.length > max) {
      this.toastr.error(`Select between 1 and ${max} roles`, 'Error', { timeOut: 3000 });
      return; // Don't send invalid data
    }

    // update role id's in channelInfo
    this.channelInfo$.subscribe(channel => {
      if (channel) {
        channel.user_roles = selectedRoleIds;
      }
    });

    this.botService.sendCommand(
      this.guildId,
      'channel user set',
      JSON.stringify(selectedRoleIds), // Send as JSON string if backend expects that
      this.channelId
    );
  }

  toggleUserConfirmationMessage(): void {
    this.botService.sendCommand(this.guildId, 'toggle user_confirmation_message', '', this.channelId);
  }

  toggleOrganizerUnconfirmedMessages(): void {
    this.botService.sendCommand(this.guildId, 'toggle organizer_unconfirmed_messages', '', this.channelId);
  }

  toggleThreads(): void {
    this.botService.sendCommand(this.guildId, this.threadCreateStatus ? 'thread disable': 'thread enable', '1', this.channelId);
    this.threadCreateStatus = !this.threadCreateStatus
  }

  toggleThreadAutodelete(): void {
    this.botService.sendCommand(this.guildId, 'thread autodelete', this.threadAutoDelStatus ? 'off' : 'on', this.channelId);
    this.threadAutoDelStatus = !this.threadAutoDelStatus
  }

  toggleExportEventOnClose(): void {
    this.botService.sendCommand(this.guildId, 'export_event toggle', 'export_event toggle', this.channelId);
  }

  toggleUserChoices(): void {
    this.botService.sendCommand(this.guildId, 'toggle show_user_choices', '', this.channelId);
  }

  toggleFooterTime(): void {
    this.botService.sendCommand(this.guildId, 'toggle footer_time', '', this.channelId);
  }

  toggleEventlClosure(): void {
    this.botService.sendCommand(this.guildId, 'event_close toggle', '', this.channelId);
  }

  toggleHereNotification(): void {
    this.botService.sendCommand(this.guildId, 'toggle here_notification', '', this.channelId);
  }

  toggleScheduledEvents(): void {
    this.botService.sendCommand(this.guildId, 'toggle discord_events', '', this.channelId);
  }

  toggleEventRoles(): void {
    this.botService.sendCommand(this.guildId, 'toggle event_roles', '', this.channelId);
  }

  toggleEventCreateOverlapNote(): void {
    this.botService.sendCommand(this.guildId, 'toggle event_create_overlap_note', '', this.channelId);
  }

  toggleShowUserChoices(): void {
    this.botService.sendCommand(this.guildId, 'toggle show_user_choices', '', this.channelId);
  }

  setSignupNumberDisplay(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'show_sign_up_numbers', eventTarget.toString(), this.channelId);
  }

  setConfirmBehavior(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'confirm_behavior set', eventTarget.toString(), this.channelId);
  }

  setDisplayNames(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'display_names set', eventTarget.toString(), this.channelId);
  }

  setSignupNumberBehavior(){
    const sign_up_behavior = (this.countBehavior + this.resetBehavior).toString();
    this.botService.sendCommand(this.guildId, 'sign_up_number_behavior set', sign_up_behavior, this.channelId);
    this.resetBehaviorCurrent = this.resetBehavior;
    this.countBehaviorCurrent = this.countBehavior;
  }

  setExportFileConfig(){
    const exportConfig = (this.exportFile + this.exportSignupState).toString();
    this.botService.sendCommand(this.guildId, 'export_event add_file', exportConfig, this.channelId);
    this.exportFileCurrent = this.exportFile;
    this.exportSignupStateCurrent = this.exportSignupState;
  }

  setCleanMode() {
    const clean_mode = (this.organizerDeleteBehavior + this.userDeleteBehavior).toString();
    this.botService.sendCommand(this.guildId, 'clean_mode set', clean_mode, this.channelId);
    this.organizerDeleteBehaviorCurrent = this.organizerDeleteBehavior;
    this.userDeleteBehaviorCurrent = this.userDeleteBehavior;
  }

  setEventDeletionTime(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'event_deletion_time set', (eventTarget as HTMLInputElement).value, this.channelId);
  }

  setSignupCooldown(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'signup_cooldown set', (eventTarget as HTMLInputElement).value, this.channelId);
  }

  onEventDeletionTimeInput(input: HTMLInputElement) {
    const newValue = +input.value; // Convert the input value to a number
    const max = this.guild.has_premium ? 60 : 14; // Maximum value

    // Subscribe to the channelInfo Observable to get the current value
    this.channelInfo$.pipe(take(1)).subscribe((channelInfo) => {
      // Check if the new value exceeds the maximum
      if (newValue > max) {
        // Reset the value to the previous value
        input.value = (channelInfo.event_deletion_time).toString();
      } else {
        // Update the channelInfo property with the new value
        channelInfo.event_deletion_time = newValue;
      }
    });
  }

  setDefaultTemplate(eventTarget: EventTarget): void {
    this.botService.sendCommand(this.guildId, 'default_signup_template set', (eventTarget as HTMLInputElement).value.toString(), this.channelId);
  }

  removeRepeatingEvent(eventTarget: string): void {
    this.botService.sendCommand(this.guildId, 'repeating_event remove', eventTarget, this.channelId, () => {
      this.channelInfo$.subscribe(channelInfo => {
        channelInfo.repeating_events = channelInfo.repeating_events.filter(event => event.id !== eventTarget);
      })
    });
  }

  private loadVoiceSettings(channel: ChannelConfig): void {
    const defaultCategory = channel.categories && channel.categories.length > 0 ? channel.categories[0].id : '';
    if (!channel.voice) {
      this.voiceName = '${event_title}';
      this.voiceTime = '5m';
      this.voiceCategoryId = defaultCategory;
      this.voiceLimit = null;
      this.voiceViewRestriction = false;
      this.voiceConnectRestriction = false;
      this.validateVoiceTimeInput();
      return;
    }

    this.voiceName = channel.voice.name || '${event_title}';
    this.voiceTime = this.secondsToDuration(channel.voice.time);
    this.voiceCategoryId = channel.voice.category || defaultCategory;
    this.voiceLimit = channel.voice.limit;
    this.voiceViewRestriction = channel.voice.view_restriction;
    this.voiceConnectRestriction = channel.voice.connect_restriction;
    this.validateVoiceTimeInput();
  }

  private secondsToDuration(seconds: number): string {
    if (!seconds || seconds <= 0) {
      return '5m';
    }

    const units = [
      { suffix: 'w', value: 604800 },
      { suffix: 'd', value: 86400 },
      { suffix: 'h', value: 3600 },
      { suffix: 'm', value: 60 },
    ];

    let remaining = seconds;
    const parts: string[] = [];
    for (const unit of units) {
      const amount = Math.floor(remaining / unit.value);
      if (amount > 0) {
        parts.push(`${amount}${unit.suffix}`);
        remaining -= amount * unit.value;
      }
    }

    return parts.length ? parts.join(' ') : `${Math.floor(seconds / 60)}m`;
  }

  private loadNotificationAndLoggingSettings(channel: ChannelConfig): void {
    const defaultTextChannel = channel.channels && channel.channels.length > 0 ? channel.channels[0].id : '';
    this.eventCreateDefaults = {
      content: channel.event_create_defaults?.content ?? this.eventCreateDefaults.content,
      embed: channel.event_create_defaults?.embed ?? this.eventCreateDefaults.embed,
    };
    this.eventCreateNotifyChannelId = this.channelIdString(channel.event_creation?.channel_id);
    this.loadEventCreateDelete(channel);
    this.setDefaultEventCreateMessages(channel);

    this.eventStartDefaults = {
      c_c: channel.event_start_defaults?.c_c ?? this.eventStartDefaults.c_c,
      c_ec: channel.event_start_defaults?.c_ec ?? this.eventStartDefaults.c_ec,
      dm_c: channel.event_start_defaults?.dm_c ?? this.eventStartDefaults.dm_c,
      dm_ec: channel.event_start_defaults?.dm_ec ?? this.eventStartDefaults.dm_ec,
    };
    this.eventStartEdit = null;
    this.resetEventStartAddForm();

    if (channel.cancel_notification) {
      this.eventCancelNotifyType = channel.cancel_notification.type || 'Channel';
      this.eventCancelNotifyTime = this.secondsToDuration(channel.cancel_notification.seconds);
      this.eventCancelNotifySignups = channel.cancel_notification.signups;
      this.eventCancelNotifyChannelId = this.channelIdString(channel.cancel_notification.channel_id);
      if (channel.cancel_notification.delete_after_seconds) {
        this.eventCancelDeleteMode = 'after';
        this.eventCancelDeleteAfter = this.deleteAfterLabel(channel.cancel_notification.delete_after_seconds);
      } else {
        this.eventCancelDeleteMode = 'never';
        this.eventCancelDeleteAfter = '';
      }
    } else {
      this.eventCancelNotifyType = 'Channel';
      this.eventCancelNotifyTime = '15m';
      this.eventCancelNotifySignups = 0;
      this.eventCancelNotifyChannelId = '';
      this.eventCancelDeleteMode = 'never';
      this.eventCancelDeleteAfter = '';
    }
    this.onEventCancelNotifyTimeChanged();
    this.onEventCancelDeleteChanged();

    this.loggingChannelId = this.channelIdString(channel.log?.channel_id) || defaultTextChannel;
    this.logEventCreation = channel.log?.event_creation ?? true;
    this.logEventEdit = channel.log?.event_edit ?? true;
    this.logEventClosed = channel.log?.event_closed ?? true;
    this.logEventOpened = channel.log?.event_opened ?? true;
    this.logEventDeleted = channel.log?.event_deleted ?? true;
    this.logUserSignUp = channel.log?.user_sign_up ?? true;
    this.logUserSignOff = channel.log?.user_sign_off ?? true;
  }

  private loadEventCreateDelete(channel: ChannelConfig): void {
    const creation = channel.event_creation;
    if (creation?.delete_after_seconds) {
      this.eventCreateDeleteMode = 'after';
      this.eventCreateDeleteAfter = this.deleteAfterLabel(creation.delete_after_seconds);
    } else if (creation?.delete_when === 'event_start' || creation?.delete_when === 'event_deletion') {
      this.eventCreateDeleteMode = creation.delete_when;
      this.eventCreateDeleteAfter = '';
    } else {
      this.eventCreateDeleteMode = 'never';
      this.eventCreateDeleteAfter = '';
    }
    this.onEventCreateDeleteChanged();
  }

  private deleteAfterLabel(seconds: number | null | undefined): string {
    if (!seconds || seconds <= 0) {
      return '';
    }
    if (seconds < 60) {
      return '1m';
    }
    return this.secondsToDuration(seconds);
  }

  private mapStateFromBackend(state: string): string {
    const mapping: {[key: string]: string} = {
      attending: 'attending',
      attending_r: 'attending role',
      attending_s: 'attending status',
      nattending: 'not attending',
      confirmed: 'confirmed',
    };
    return mapping[state] || 'attending';
  }

  onVoiceTimeChanged(): void {
    this.validateVoiceTimeInput();
  }

  private validateVoiceTimeInput(): void {
    this.voiceTimeInvalid = !this.isValidVoiceTime(this.voiceTime);
  }

  private isValidVoiceTime(value: string): boolean {
    if (!value) {
      return false;
    }

    const normalized = value.trim().toLowerCase();
    // Valid examples: 5m, 1h 30m, 1w 2d 3h
    const pattern = /^(\d+\s*[wdhm]\s*)+$/;
    if (!pattern.test(normalized)) {
      return false;
    }

    const matches = normalized.match(/(\d+)\s*([wdhm])/g);
    if (!matches || matches.length === 0) {
      return false;
    }

    let totalSeconds = 0;
    for (const entry of matches) {
      const match = entry.match(/(\d+)\s*([wdhm])/);
      if (!match) {
        return false;
      }
      const amount = Number(match[1]);
      const unit = match[2];
      if (!Number.isInteger(amount) || amount <= 0) {
        return false;
      }
      if (unit === 'w') {
        totalSeconds += amount * 604800;
      } else if (unit === 'd') {
        totalSeconds += amount * 86400;
      } else if (unit === 'h') {
        totalSeconds += amount * 3600;
      } else if (unit === 'm') {
        totalSeconds += amount * 60;
      } else {
        return false;
      }
    }

    return totalSeconds > 0;
  }

  onEventStartNotifyTimeChanged(): void {
    this.eventStartNotifyTimeInvalid = !this.isValidVoiceTime(this.eventStartNotifyTime);
  }

  onEventStartEditTimeChanged(): void {
    if (!this.eventStartEdit) {
      return;
    }
    this.eventStartEdit.timeInvalid = !this.isValidVoiceTime(this.eventStartEdit.time);
  }

  onEventCancelNotifyTimeChanged(): void {
    this.eventCancelNotifyTimeInvalid = !this.isValidVoiceTime(this.eventCancelNotifyTime);
  }

  onEventCreateDeleteChanged(): void {
    this.eventCreateDeleteInvalid = this.eventCreateDeleteMode === 'after'
      && !this.isValidVoiceTime(this.eventCreateDeleteAfter);
  }

  onEventCancelDeleteChanged(): void {
    this.eventCancelDeleteInvalid = this.eventCancelDeleteMode === 'after'
      && !this.isValidVoiceTime(this.eventCancelDeleteAfter);
  }

  canAddEventStartNotification(channelInfo: ChannelConfig): boolean {
    if (this.guild?.has_premium) {
      return true;
    }
    return (channelInfo.notifications || []).length === 0;
  }

  eventStartAddLimitReached(channelInfo: ChannelConfig): boolean {
    return !this.guild?.has_premium && (channelInfo.notifications || []).length >= 1;
  }

  isEditingEventStartNotification(notification: EventStartNotificationConfig): boolean {
    return this.eventStartEdit?.identifier === notification.identifier;
  }

  editingEventStartDraft(notification: EventStartNotificationConfig): EventStartNotificationDraft | null {
    if (this.eventStartEdit?.identifier !== notification.identifier) {
      return null;
    }
    return this.eventStartEdit;
  }

  canStartAnotherEventStartEdit(notification: EventStartNotificationConfig): boolean {
    return !this.eventStartEdit || this.eventStartEdit.identifier === notification.identifier;
  }

  beginEditEventStartNotification(notification: EventStartNotificationConfig): void {
    if (!this.canStartAnotherEventStartEdit(notification)) {
      return;
    }
    this.clearChannelFilter(this.startEditChannelFilter);
    this.eventStartEdit = {
      identifier: notification.identifier,
      state: this.mapStateFromBackend(notification.state),
      type: notification.type || 'Channel',
      time: this.secondsToDuration(notification.seconds),
      timeInvalid: false,
      deleteWhen: notification.delete_when || 'never',
      channelId: this.channelIdString(notification.channel_id),
      channelText: this.startMessageOrDefault(notification.c_c, 'c_c'),
      channelEmbed: this.startMessageOrDefault(notification.c_ec, 'c_ec'),
      dmText: this.startMessageOrDefault(notification.dm_c, 'dm_c'),
      dmEmbed: this.startMessageOrDefault(notification.dm_ec, 'dm_ec'),
    };
    this.onEventStartEditTimeChanged();
  }

  cancelEditEventStartNotification(): void {
    this.eventStartEdit = null;
    this.eventStartSaving = false;
  }

  saveEventStartNotification(channelInfo: ChannelConfig): void {
    const draft = this.eventStartEdit;
    if (!draft || this.eventStartSaving) {
      return;
    }
    if (!this.isValidVoiceTime(draft.time)) {
      draft.timeInvalid = true;
      this.toastr.error('Invalid event start notification time.', 'Error', { timeOut: 3000 });
      return;
    }
    this.eventStartSaving = true;
    const payload = {
      identifier: draft.identifier,
      state: draft.state,
      type: draft.type,
      time: draft.time.trim(),
      delete_when: draft.deleteWhen === 'never' ? null : draft.deleteWhen,
      channel_id: this.notificationUsesChannel(draft.type) ? draft.channelId || null : null,
      message: {
        c_c: draft.channelText,
        c_ec: draft.channelEmbed,
        dm_c: draft.dmText,
        dm_ec: draft.dmEmbed,
      },
    };
    const finishSaving = (): void => {
      this.eventStartSaving = false;
    };
    this.botService.sendCommand(
      this.guildId,
      'event_start_notification edit',
      JSON.stringify(payload),
      this.channelId,
      () => {
        this.botService.getGuildChannel(this.guildId, this.channelId).pipe(take(1)).subscribe({
          next: updatedChannel => {
            channelInfo.notifications = updatedChannel.notifications || [];
            if (this.eventStartEdit?.identifier === draft.identifier) {
              this.eventStartEdit = null;
            }
          },
          error: finishSaving,
          complete: finishSaving,
        });
      },
      finishSaving
    );
  }

  saveVoiceSettings(channelInfo: ChannelConfig): void {
    if (!this.guild?.has_premium) {
      this.toastr.error('Voice channels require premium.', 'Error', { timeOut: 3000 });
      return;
    }

    if (!this.voiceName || !this.voiceTime || !this.voiceCategoryId) {
      this.toastr.error('Voice name, time and category are required.', 'Error', { timeOut: 3000 });
      return;
    }
    if (!this.isValidVoiceTime(this.voiceTime)) {
      this.voiceTimeInvalid = true;
      this.toastr.error('Invalid time format. Use values like "5m" or "1h 30m".', 'Error', { timeOut: 3000 });
      return;
    }

    const payload = {
      name: this.voiceName,
      time: this.voiceTime.trim(),
      category: this.voiceCategoryId,
      limit: this.voiceLimit ? this.voiceLimit : null,
      view_restriction: this.voiceViewRestriction,
      connect_restriction: this.voiceConnectRestriction,
    };

    this.botService.sendCommand(
      this.guildId,
      'event_voice set',
      JSON.stringify(payload),
      this.channelId,
      () => {
        channelInfo.voice = {
          name: payload.name,
          time: channelInfo.voice?.time || 0,
          category: payload.category,
          limit: payload.limit,
          view_restriction: payload.view_restriction,
          connect_restriction: payload.connect_restriction,
        };
      }
    );
  }

  removeVoiceSettings(channelInfo: ChannelConfig): void {
    this.botService.sendCommand(this.guildId, 'event_voice remove', '', this.channelId, () => {
      channelInfo.voice = null;
      this.loadVoiceSettings(channelInfo);
    });
  }

  saveEventCreateNotification(channelInfo: ChannelConfig): void {
    if (this.eventCreateDeleteInvalid) {
      this.toastr.error('Invalid auto-delete time.', 'Error', { timeOut: 3000 });
      return;
    }
    const deleteAfter = this.eventCreateDeleteMode === 'after' ? this.eventCreateDeleteAfter.trim() : null;
    const deleteWhen = this.eventCreateDeleteMode === 'event_start' || this.eventCreateDeleteMode === 'event_deletion'
      ? this.eventCreateDeleteMode
      : null;
    const payload = {
      channel_id: this.eventCreateNotifyChannelId || null,
      // Custom message editing is premium-only. Non-premium uses backend defaults.
      content: this.guild?.has_premium ? this.eventCreateNotifyMessage : '',
      embed: this.guild?.has_premium ? this.eventCreateNotifyEmbed : '',
      delete_when: deleteWhen,
      delete_after_seconds: deleteAfter,
    };
    this.botService.sendCommand(this.guildId, 'event_create_notification add', JSON.stringify(payload), this.channelId, () => {
      channelInfo.event_creation = {
        channel_id: payload.channel_id ? String(payload.channel_id) : null,
        content: payload.content,
        embed: payload.embed,
        delete_when: deleteWhen,
        delete_after_seconds: channelInfo.event_creation?.delete_after_seconds ?? null,
      };
    });
  }

  removeEventCreateNotification(channelInfo: ChannelConfig): void {
    this.botService.sendCommand(this.guildId, 'event_create_notification remove', '', this.channelId, () => {
      channelInfo.event_creation = null;
      this.setDefaultEventCreateMessages(channelInfo);
    });
  }

  addEventStartNotification(channelInfo: ChannelConfig): void {
    if (this.eventStartAdding) {
      return;
    }
    if (!this.isValidVoiceTime(this.eventStartNotifyTime)) {
      this.eventStartNotifyTimeInvalid = true;
      this.toastr.error('Invalid event start notification time.', 'Error', { timeOut: 3000 });
      return;
    }
    this.eventStartAdding = true;
    const payload = {
      state: this.eventStartNotifyState,
      type: this.eventStartNotifyType,
      time: this.eventStartNotifyTime.trim(),
      delete_when: this.eventStartDeleteWhen === 'never' ? null : this.eventStartDeleteWhen,
      channel_id: this.eventStartUsesChannel() ? this.eventStartNotifyChannelId || null : null,
      message: {
        c_c: this.eventStartNotifyChannelText,
        c_ec: this.eventStartNotifyChannelEmbed,
        dm_c: this.eventStartNotifyDmText,
        dm_ec: this.eventStartNotifyDmEmbed,
      },
    };
    const finishAdding = (): void => {
      this.eventStartAdding = false;
    };
    this.botService.sendCommand(
      this.guildId,
      'event_start_notification add',
      JSON.stringify(payload),
      this.channelId,
      () => {
        this.botService.getGuildChannel(this.guildId, this.channelId).pipe(take(1)).subscribe({
          next: updatedChannel => {
            channelInfo.notifications = updatedChannel.notifications || [];
            this.resetEventStartAddForm();
          },
          error: finishAdding,
          complete: finishAdding,
        });
      },
      finishAdding
    );
  }

  removeEventStartNotification(identifier: string): void {
    this.botService.sendCommand(this.guildId, 'event_start_notification remove', identifier, this.channelId, () => {
      this.channelInfo$.pipe(take(1)).subscribe(channelInfo => {
        channelInfo.notifications = (channelInfo.notifications || []).filter(entry => entry.identifier !== identifier);
      });
    });
  }

  saveEventCancelNotification(channelInfo: ChannelConfig): void {
    if (!this.guild?.has_premium) {
      this.toastr.error('Cancel notification requires premium.', 'Error', { timeOut: 3000 });
      return;
    }
    if (!this.isValidVoiceTime(this.eventCancelNotifyTime)) {
      this.eventCancelNotifyTimeInvalid = true;
      this.toastr.error('Invalid event cancel notification time.', 'Error', { timeOut: 3000 });
      return;
    }
    if (this.eventCancelDeleteInvalid) {
      this.toastr.error('Invalid auto-delete time.', 'Error', { timeOut: 3000 });
      return;
    }
    const deleteAfter = this.eventCancelDeleteMode === 'after' ? this.eventCancelDeleteAfter.trim() : null;
    const payload = {
      type: this.eventCancelNotifyType,
      time: this.eventCancelNotifyTime.trim(),
      signups: this.eventCancelNotifySignups,
      delete_after_seconds: deleteAfter,
      channel_id: this.eventCancelUsesChannel() && this.eventCancelNotifyChannelId
        ? this.eventCancelNotifyChannelId
        : null,
    };
    this.botService.sendCommand(this.guildId, 'event_cancel_notification add', JSON.stringify(payload), this.channelId, () => {
      channelInfo.cancel_notification = {
        seconds: channelInfo.cancel_notification?.seconds || 0,
        type: payload.type,
        signups: payload.signups,
        identifier: channelInfo.cancel_notification?.identifier || '',
        delete_after_seconds: channelInfo.cancel_notification?.delete_after_seconds,
        channel_id: payload.channel_id,
      };
    });
  }

  removeEventCancelNotification(channelInfo: ChannelConfig): void {
    this.botService.sendCommand(this.guildId, 'event_cancel_notification remove', '', this.channelId, () => {
      channelInfo.cancel_notification = null;
    });
  }

  saveLoggingSetup(channelInfo: ChannelConfig): void {
    if (!this.guild?.has_premium) {
      this.toastr.error('Logging setup requires premium.', 'Error', { timeOut: 3000 });
      return;
    }
    if (!this.loggingChannelId) {
      this.toastr.error('Logging channel is required.', 'Error', { timeOut: 3000 });
      return;
    }
    const payload = {
      channel_id: this.loggingChannelId,
      event_creation: this.logEventCreation,
      event_edit: this.logEventEdit,
      event_closed: this.logEventClosed,
      event_opened: this.logEventOpened,
      event_deleted: this.logEventDeleted,
      user_sign_up: this.logUserSignUp,
      user_sign_off: this.logUserSignOff,
    };
    this.botService.sendCommand(this.guildId, 'logging setup', JSON.stringify(payload), this.channelId, () => {
      channelInfo.log = payload;
    });
  }

  removeLoggingSetup(channelInfo: ChannelConfig): void {
    this.botService.sendCommand(this.guildId, 'logging remove', '', this.channelId, () => {
      channelInfo.log = null;
    });
  }

  formatNotificationState(state: string): string {
    const mapping: {[key: string]: string} = {
      attending: 'Attending',
      attending_r: 'Attending (Role)',
      attending_s: 'Attending (Status)',
      nattending: 'Not attending',
      confirmed: 'Confirmed',
    };
    return mapping[state] || state;
  }

  formatStartDeleteWhen(deleteWhen: string | null | undefined): string {
    const match = this.startDeleteOptions.find(option => option.value === (deleteWhen || 'never'));
    return match ? match.label : 'Never';
  }

  eventStartUsesChannel(): boolean {
    return this.eventStartNotifyType.includes('Channel');
  }

  eventCancelUsesChannel(): boolean {
    return this.eventCancelNotifyType.includes('Channel');
  }

  eventStartUsesDm(): boolean {
    return this.eventStartNotifyType.includes('DM');
  }

  notificationUsesChannel(type: string): boolean {
    return type.includes('Channel');
  }

  notificationUsesDm(type: string): boolean {
    return type.includes('DM');
  }

  getNotificationTextPreview(value: string | undefined, max = 140): string {
    if (!value) {
      return '-';
    }
    const normalized = value.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return '-';
    }
    return normalized.length > max ? `${normalized.slice(0, max)}...` : normalized;
  }

  getStartNotificationTextPreview(
    notification: { c_c?: string; c_ec?: string; dm_c?: string; dm_ec?: string },
    field: 'c_c' | 'c_ec' | 'dm_c' | 'dm_ec',
    max = 140
  ): string {
    const value = (notification[field] || '').trim();
    const fallback = (this.eventStartDefaults[field] || '').trim();
    if (!value || value === fallback) {
      return 'Default';
    }
    return this.getNotificationTextPreview(notification[field], max);
  }

  private startMessageOrDefault(value: string | undefined, field: 'c_c' | 'c_ec' | 'dm_c' | 'dm_ec'): string {
    return value && value.trim() ? value : this.eventStartDefaults[field];
  }

  private resetEventStartAddForm(): void {
    this.eventStartNotifyState = 'attending';
    this.eventStartNotifyType = 'Channel';
    this.eventStartNotifyChannelId = '';
    this.eventStartNotifyTime = '15m';
    this.eventStartDeleteWhen = 'never';
    this.setDefaultEventStartMessages();
    this.onEventStartNotifyTimeChanged();
  }

  private setDefaultEventStartMessages(): void {
    this.eventStartNotifyChannelText = this.eventStartDefaults.c_c;
    this.eventStartNotifyChannelEmbed = this.eventStartDefaults.c_ec;
    this.eventStartNotifyDmText = this.eventStartDefaults.dm_c;
    this.eventStartNotifyDmEmbed = this.eventStartDefaults.dm_ec;
  }

  private setDefaultEventCreateMessages(channel: ChannelConfig): void {
    this.eventCreateNotifyMessage = channel.event_creation?.content || this.eventCreateDefaults.content;
    this.eventCreateNotifyEmbed = channel.event_creation?.embed || this.eventCreateDefaults.embed;
  }

  canManageChannelConfigs(): boolean {
    return !!this.guild?.owner;
  }

  isChannelSetupLimitReached(): boolean {
    if (!this.guild) {
      return true;
    }
    if (this.guild.has_premium) {
      return false;
    }
    return this.guild.channels.length >= this.guild.limits.max_event_channels;
  }

  setupChannel(): void {
    if (!this.canManageChannelConfigs()) {
      this.toastr.error('Only the server owner can manage channel setup.', 'Error', { timeOut: 3000 });
      return;
    }
    if (this.isChannelSetupLimitReached()) {
      this.toastr.error('Maximum number of channels reached.', 'Error', { timeOut: 3000 });
      return;
    }
    if (!this.setupTargetChannelId || !this.setupOrganizerRoleId || !this.setupUserRoleId || !this.setupChannelType) {
      this.toastr.error('Channel, type, organizer role and user role are required.', 'Error', { timeOut: 3000 });
      return;
    }

    const targetChannelId = this.setupTargetChannelId;
    const payload = {
      channel_id: targetChannelId,
      channel_type: this.setupChannelType,
      organizer_role: this.setupOrganizerRoleId,
      user_roles: [this.setupUserRoleId],
    };
    this.botService.sendCommand(this.guildId, 'channel setup', JSON.stringify(payload), undefined, () => {
      const targetChannel = this.channelSetupInfo?.channels?.find((entry) => entry.id === targetChannelId);
      if (this.guild && targetChannel && !this.guild.channels.some((entry) => entry.id === targetChannelId)) {
        this.guild.channels.push({
          id: targetChannelId,
          name: targetChannel.name,
          default_template: '0',
        });
      }
      this.router.navigate(['/guilds', this.guildId, 'channel', targetChannelId]);
    });
  }

  removeCurrentChannel(channel: ChannelConfig): void {
    if (!this.canManageChannelConfigs()) {
      this.toastr.error('Only the server owner can manage channel setup.', 'Error', { timeOut: 3000 });
      return;
    }
    this.channelToDeleteLabel = channel.channel_name || this.channelId;
    this.eventsImpacted = (channel.events || []).filter((event) => !!event?.id);
    this.repeatingEventsImpacted = (channel.repeating_events || []).filter((event) => !!event?.id);
    this.dialogDeleteChannelRef = this.dialog.open(this.deleteChannelDialog, {
      panelClass: 'raid-organizer-dialog',
    });

    this.dialogDeleteChannelRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      this.botService.sendCommand(this.guildId, 'channel remove', this.channelId, undefined, () => {
        if (this.guild) {
          this.guild.channels = this.guild.channels.filter((entry) => entry.id !== this.channelId);
        }
        this.router.navigate(['/guilds', this.guildId, 'overview']);
      });
    });
  }

  getEventUrl(messageId: string): string {
    return `https://discord.com/channels/${this.guildId}/${this.channelId}/${messageId}`;
  }

  clearChannelFilter(control: FormControl): void {
    if (control.value) {
      control.setValue('');
    }
  }

  clearSetupChannelFilter(): void {
    this.clearChannelFilter(this.setupChannelFilter);
  }

  channelName(channelId: string | number | null | undefined, channels: ChannelPreview[] | undefined): string {
    const id = this.channelIdString(channelId);
    if (!id) {
      return 'Event channel';
    }
    return (channels || []).find(channel => channel.id === id)?.name || 'Unknown channel';
  }

  private refreshTextChannelFilters(): void {
    this.applyExportChannelFilter();
    this.applyCreateChannelFilter();
    this.applyCancelChannelFilter();
    this.applyLogChannelFilter();
    this.applyStartChannelFilter();
    this.applyStartEditChannelFilter();
  }

  private applyExportChannelFilter(): void {
    this.filteredExportChannels = this.filterChannelsByName(this.textChannels, this.exportChannelFilter.value);
  }

  private applyCreateChannelFilter(): void {
    this.filteredCreateChannels = this.filterChannelsByName(
      this.textChannels,
      this.createChannelFilter.value,
      this.eventCreateNotifyChannelId,
    );
  }

  private applyCancelChannelFilter(): void {
    this.filteredCancelChannels = this.filterChannelsByName(
      this.textChannels,
      this.cancelChannelFilter.value,
      this.eventCancelNotifyChannelId,
    );
  }

  private applyLogChannelFilter(): void {
    this.filteredLogChannels = this.filterChannelsByName(
      this.textChannels,
      this.logChannelFilter.value,
      this.loggingChannelId,
    );
  }

  private applyStartChannelFilter(): void {
    this.filteredStartChannels = this.filterChannelsByName(
      this.textChannels,
      this.startChannelFilter.value,
      this.eventStartNotifyChannelId,
    );
  }

  private applyStartEditChannelFilter(): void {
    this.filteredStartEditChannels = this.filterChannelsByName(
      this.textChannels,
      this.startEditChannelFilter.value,
      this.eventStartEdit?.channelId,
    );
  }

  private applySetupChannelFilter(): void {
    this.filteredSetupChannels = this.filterChannelsByName(
      this.setupChannels,
      this.setupChannelFilter.value,
      this.setupTargetChannelId,
    );
  }

  private channelIdString(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value);
  }

  private filterChannelsByName(
    channels: ChannelPreview[],
    term: string | null,
    keepId?: string | null,
  ): ChannelPreview[] {
    const query = (term || '').trim().toLowerCase();
    const pinnedId = this.channelIdString(keepId);
    if (!query) {
      return channels;
    }
    return channels.filter(channel => channel.id === pinnedId || channel.name.toLowerCase().includes(query));
  }

  private initializeChannelSetupDefaults(info: ChannelSetupInfo): void {
    this.setupChannels = info.channels || [];
    this.applySetupChannelFilter();
    if (!this.setupTargetChannelId && info.channels?.length > 0) {
      this.setupTargetChannelId = info.channels[0].id;
    }
    const assignableRoles = (info.roles || []).filter((role) => role.name !== '@everyone');
    if (!this.setupOrganizerRoleId) {
      if (assignableRoles.length > 0) {
        this.setupOrganizerRoleId = assignableRoles[0].id;
      }
    }
    if (!this.setupUserRoleId && assignableRoles.length > 0) {
      this.setupUserRoleId = assignableRoles[0].id;
    }
  }

  private scrollToSection(): void {
    const id = this.sectionFragment;
    if (!id) {
      return;
    }
    let attempts = 0;
    const tick = () => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < 20) {
        setTimeout(tick, 50);
      }
    };
    setTimeout(tick, 0);
  }
}
